// intake.view — form Terima Servis (port terimaForm v0.18). Tanpa akses DB/logika uang.
import { rpFmt, rpVal, esc, wa62, genNo, fillTpl } from '@domain/format';
import { currentOutlet, currentUser } from '@core/session';
import { fotoUnitPicker } from '@ui/fotounit';
import { openWA } from '@core/wa';
import { waTpl } from '@core/settings';
import { toast } from '@ui/toast';
import { openModal } from '@ui/modal';
import { previewTandaTerima } from '@modules/documents/documents.view';
import { refreshCounts } from '@core/nav';
import * as repo from './intake.repo';
import { submitIntake, type IntakeForm } from './intake.service';
import { autosave, collectForm, bolehKirimOtomatis, readDraft, applyDraft, clearDraft, isOnline, onBackOnline } from '@core/draft';
import { TiketKembar } from './intake.service';

const JENIS_OPTS = ['HP', 'Tablet', 'Laptop', 'Smartwatch', 'Lainnya'];
const KELENGKAPAN_OPTS = ['Unit', 'Baterai', 'Charger', 'Kabel USB', 'SIM', 'Memori', 'Dus', 'Softcase/Case', 'Lainnya'];
const PRIO: [string, string][] = [['P0', 'P0 — Darurat'], ['P1', 'P1 — Tinggi'], ['P2', 'P2 — Normal'], ['P3', 'P3 — Rendah/Hold']];

let pickedOutlet: string | null = null; // pilihan cabang utk Owner/Manager (scope Semua)

export async function renderIntake(root: HTMLElement): Promise<void> {
  const u = currentUser()!;
  const isOM = ['Owner', 'Manager'].includes(u.role);
  root.innerHTML = `<div class="crumb">Terima Servis</div>
    <div class="ttl">➕ Terima Servis</div><div class="panel"><div class="empty">Memuat data…</div></div>`;

  let customers: Awaited<ReturnType<typeof repo.listCustomers>>;
  let outlets: Awaited<ReturnType<typeof repo.listOutlets>>;
  let services: Awaited<ReturnType<typeof repo.listServices>> = [];
  try { [customers, outlets, services] = await Promise.all([repo.listCustomers(), repo.listOutlets(), repo.listServices()]); }
  catch (e) { (root.querySelector('.panel') as HTMLElement).innerHTML = `<div class="err">Gagal memuat: ${esc((e as Error).message)}</div>`; return; }

  const outlet = pickedOutlet ?? currentOutlet() ?? outlets[0]?.kode ?? 'PKP';
  let techs: Awaited<ReturnType<typeof repo.listTechnicians>>;
  try { techs = await repo.listTechnicians(outlet); } catch { techs = []; }

  const custOpts = customers.map((c) => `<option value="${c.id}">${esc(c.nama)} · ${esc(c.kat)}</option>`).join('');
  const dugaanOpts = services.map((s) => `<option value="${s.id}">${esc(s.nama)}</option>`).join('');
  const custWaMap: Record<number, string> = {}; for (const c of customers) custWaMap[c.id] = c.wa ?? c.telp ?? '';
  const tekOpts = techs.map((t) => `<option value="${esc(t.nama)}">${esc(t.nama)}</option>`).join('');
  const jenisOpts = JENIS_OPTS.map((j) => `<option>${j}</option>`).join('');
  const prioOpts = PRIO.filter(([v]) => v !== 'P0' || isOM).map(([v, l]) => `<option value="${v}" ${v === 'P2' ? 'selected' : ''}>${l}</option>`).join('');
  const kelBoxes = KELENGKAPAN_OPTS.map((k, i) => `<label style="display:inline-flex;align-items:center;gap:4px;margin:0 10px 6px 0;font-size:13px"><input type="checkbox" class="f_kel" id="f_kel_${i}" value="${esc(k)}" ${k === 'Unit' ? 'checked' : ''}>${esc(k)}</label>`).join('');
  const noPreview = genNo(outlet);
  // Cabang: staf → terkunci; Owner/Manager → pilih.
  const outletField = (!currentOutlet())
    ? `<select id="f_outlet">${outlets.map((o) => `<option value="${esc(o.kode)}" ${o.kode === outlet ? 'selected' : ''}>${esc(o.nama)}</option>`).join('')}</select>`
    : `<input value="${esc(outlets.find((o) => o.kode === outlet)?.nama ?? outlet)}" disabled>`;

  root.innerHTML = `
    <div class="crumb">${esc(outlets.find((o) => o.kode === outlet)?.nama ?? outlet)} · ${esc(u.role)}</div>
    <div class="ttl">➕ Terima Servis</div>
    <div class="scope-note">Form penerimaan unit. Setelah disimpan → antrian "Diterima". Isi <b>Estimasi</b> + <b>Teknisi</b> → langsung "Dikerjakan".</div>
    <div class="panel" style="max-width:680px">
      <div class="row2">
        <div class="field"><label>No. Servis <span style="color:var(--muted);font-weight:400">(acak · anti-pemalsuan)</span></label><input id="f_no" value="${esc(noPreview)}" disabled></div>
        <div class="field"><label>Cabang</label>${outletField}</div>
      </div>

      <div style="font-weight:600;color:var(--navy);margin:6px 0">Pelanggan</div>
      <div class="field"><label>Cari Pelanggan <span style="color:var(--muted);font-weight:400">(nama / nomor — CS wajib verifikasi nomor)</span></label>
        <input id="f_lookup" placeholder="ketik nama atau nomor lalu pilih di bawah…"></div>
      <div class="field"><label>Pilih Pelanggan <span class="req">*</span></label>
        <select id="f_cust"><option value="">— pilih —</option>${custOpts}</select>
        <div id="f_custwa" style="font-size:12px;color:var(--muted);margin-top:3px"></div></div>
      <label style="display:inline-flex;align-items:center;gap:6px;font-size:13px;margin-bottom:8px"><input type="checkbox" id="f_baru"> Pelanggan baru</label>
      <div id="f_newbox" class="hidden" style="border:1px solid var(--line);border-radius:var(--radius);padding:10px;margin-bottom:8px">
        <div class="row2"><div class="field"><label>Nama baru <span class="req">*</span></label><input id="f_nnama"></div>
          <div class="field"><label>Kategori</label><select id="f_nkat"><option>User</option><option>Toko</option></select></div></div>
        <div class="field"><label>Telepon</label><div style="display:flex;gap:6px"><input id="f_ntelp" inputmode="numeric" placeholder="0812…" style="flex:1"><button type="button" class="btn" id="f_conv" style="white-space:nowrap">Convert → WA</button></div></div>
        <div class="field"><label>WhatsApp <span style="color:var(--muted);font-weight:400">(auto +62)</span></label><input id="f_nwa" inputmode="numeric" placeholder="62812…"></div>
        <div style="font-size:12px"><a href="javascript:void(0)" id="f_nmore_t">▸ Lengkapi data (opsional)</a></div>
        <div id="f_nmore" class="hidden" style="margin-top:8px">
          <div class="row2"><div class="field"><label>Tgl Lahir</label><input id="f_nlahir" type="date"></div><div class="field"><label>Email</label><input id="f_nemail"></div></div>
          <div class="field"><label>Kota</label><input id="f_nkota"></div>
          <div class="field"><label>Alamat</label><textarea id="f_nalamat"></textarea></div>
        </div>
      </div>

      <div style="font-weight:600;color:var(--navy);margin:8px 0 6px">Unit</div>
      <div class="row2"><div class="field"><label>Jenis <span class="req">*</span></label><select id="f_jenis">${jenisOpts}</select></div>
        <div class="field"><label>Merk <span class="req">*</span></label><input id="f_merk" placeholder="Samsung"></div></div>
      <div class="row2"><div class="field"><label>Model / Tipe</label><input id="f_model" placeholder="A54"></div>
        <div class="field"><label>No. Seri / IMEI</label><input id="f_sn"></div></div>
      <div class="row2"><div class="field"><label>Warna</label><input id="f_warna"></div>
        <div class="field"><label>Pola / Sandi / PIN <span style="color:var(--muted);font-weight:400">(akses teknisi)</span></label><input id="f_sandi"></div></div>
      <div class="field"><label>Kelengkapan</label><div>${kelBoxes}</div></div>
      <div class="field hidden" id="f_kelbox"><label>Kelengkapan lainnya <span style="color:var(--muted);font-weight:400">(isi manual oleh CS)</span></label><input id="f_kellain" placeholder="mis. stylus, kartu garansi, casing tambahan…"></div>
      <label style="display:flex;gap:6px;align-items:center;font-size:13px;margin:2px 0 6px;color:var(--danger)"><input type="checkbox" id="f_case"> ⚠️ Case khusus — unit tidak bisa full-test (mati total / masuk air / mesin bengkok / fisik rusak)</label>
      <div class="field hidden" id="f_casebox"><label>Keterangan case khusus</label><input id="f_caseket" placeholder="mis. mati total — tak bisa dicek fungsi penuh"></div>

      <div style="font-weight:600;color:var(--navy);margin:8px 0 6px">Keluhan</div>
      <div class="field"><label>Keluhan / Kerusakan <span class="req">*</span></label><textarea id="f_keluhan" placeholder="LCD pecah…"></textarea></div>
      <div class="field"><label>Keterangan <span style="color:var(--muted);font-weight:400">(tampil ke pelanggan: nota & WA)</span></label><textarea id="f_kondisi" placeholder="kondisi fisik awal / catatan utk pelanggan"></textarea></div>
      <div id="f_fotounit"></div>
      <div class="field"><label>Dugaan tindakan <span style="color:var(--muted);font-weight:400">(opsional — dugaan awal dari daftar Jasa; acuan teknisi)</span></label>
        <select id="f_dugaan"><option value="">— belum ada dugaan —</option>${dugaanOpts}</select></div>

      <div style="font-weight:600;color:var(--navy);margin:8px 0 6px">Estimasi & Biaya</div>
      <div class="row2"><div class="field"><label>Estimasi biaya</label><input id="f_est" value="Rp 0" inputmode="numeric"></div>
        <div class="field"><label>Estimasi selesai</label><input id="f_estdate" type="date"></div></div>
      <div class="field"><label>DP / uang muka</label><input id="f_dp" value="Rp 0" inputmode="numeric"></div>
      <div class="field" id="f_dpvia_wrap" style="display:none"><label>DP diterima via <span class="req">*</span></label>
        <select id="f_dpvia"><option>Tunai</option><option>Transfer</option><option>QRIS</option><option>Lainnya</option></select>
        <div class="scope-note">Dipakai Tutup Kasir untuk memisahkan uang laci dan uang bank.</div></div>
      <div class="field"><label>Teknisi <span style="color:var(--muted);font-weight:400">(isi bila harga sudah pasti → langsung Dikerjakan)</span></label>
        <select id="f_tek"><option value="">— belum / tugaskan nanti —</option>${tekOpts}</select></div>
      <div class="scope-note" style="margin:-2px 0 6px">Jika <b>Estimasi</b> diisi <b>DAN</b> teknisi dipilih → tiket langsung <b>Dikerjakan</b> (lewati Diagnosa & Persetujuan; harga dianggap disetujui di counter).</div>
      <div class="row2"><div class="field"><label>Prioritas</label><select id="f_prio">${prioOpts}</select></div>
        <div class="field"><label>Penerima</label><input value="${esc(u.nama)}" disabled></div></div>
      <div class="field"><label>Catatan internal</label><textarea id="f_catatan"></textarea></div>
      <div class="err" id="f_err"></div>
      <div id="f_draftbar"></div>
      <button class="btn primary" id="f_save">💾 Simpan &amp; Buat Tiket</button>
    </div>`;

  const $ = (id: string): HTMLElement => root.querySelector('#' + id) as HTMLElement;
  const val = (id: string): string => { const el = root.querySelector('#' + id) as HTMLInputElement | null; return el ? el.value : ''; };

  // Owner/Manager ganti cabang → re-render (refresh teknisi + no).
  const outletSel = root.querySelector('#f_outlet') as HTMLSelectElement | null;
  if (outletSel) outletSel.addEventListener('change', () => { pickedOutlet = outletSel.value; void renderIntake(root); });

  const baru = $('f_baru') as HTMLInputElement;
  baru.addEventListener('change', () => {
    ($('f_newbox') as HTMLElement).classList.toggle('hidden', !baru.checked);
    ($('f_cust') as HTMLElement).style.display = baru.checked ? 'none' : '';
  });
  root.querySelector('#f_conv')?.addEventListener('click', () => { (root.querySelector('#f_nwa') as HTMLInputElement).value = wa62(val('f_ntelp')); });

  // Cari pelanggan (nama/nomor) → filter opsi select. CS pilih lalu VERIFIKASI nomor yang tampil.
  const custSel = $('f_cust') as HTMLSelectElement;
  const showCustWa = (): void => {
    const id = +custSel.value; const wa = id ? custWaMap[id] : '';
    ($('f_custwa') as HTMLElement).textContent = wa ? `📞 No. HP terdaftar: ${wa} — verifikasi ke pelanggan.` : (id ? '⚠️ Pelanggan ini belum punya nomor.' : '');
  };
  custSel.addEventListener('change', showCustWa);
  $('f_lookup')?.addEventListener('input', () => {
    const q = val('f_lookup').trim().toLowerCase();
    let firstMatch = '';
    Array.from(custSel.options).forEach((o) => {
      if (!o.value) return;
      const wa = custWaMap[+o.value] ?? '';
      const digits = q.replace(/\D/g, ''); // hindari wa.includes('') yang selalu true → filter nama tak berfungsi
      const hit = !q || o.textContent!.toLowerCase().includes(q) || (digits !== '' && wa.includes(digits));
      o.hidden = !hit;
      if (hit && !firstMatch) firstMatch = o.value;
    });
    if (q && firstMatch) { custSel.value = firstMatch; showCustWa(); }
  });

  // Foto kondisi unit: diunggah saat dipilih, jalurnya ikut disimpan bersama tiket.
  const fotoBox = $('f_fotounit') as HTMLElement;
  const foto = fotoUnitPicker(fotoBox, { outlet, fase: 'terima', oleh: currentUser()?.nama ?? '' });
  const caseCb = $('f_case') as HTMLInputElement;
  caseCb.addEventListener('change', () => ($('f_casebox') as HTMLElement).classList.toggle('hidden', !caseCb.checked));
  const kelLain = Array.from(root.querySelectorAll<HTMLInputElement>('.f_kel')).find((c) => c.value === 'Lainnya');
  kelLain?.addEventListener('change', () => ($('f_kelbox') as HTMLElement).classList.toggle('hidden', !kelLain.checked));
  const moreT = root.querySelector('#f_nmore_t');
  moreT?.addEventListener('click', () => { const m = $('f_nmore'); const hid = m.classList.toggle('hidden'); moreT.textContent = hid ? '▸ Lengkapi data (opsional)' : '▾ Sembunyikan data tambahan'; });
  for (const id of ['f_est', 'f_dp']) { const el = $(id) as HTMLInputElement; el.addEventListener('input', () => { el.value = rpFmt(el.value); }); }
  // Metode DP hanya relevan bila ada DP — tampilkan saat diisi, sembunyikan saat nol.
  const dpEl = $('f_dp') as HTMLInputElement, dpWrap = $('f_dpvia_wrap') as HTMLElement | null;
  const syncDpVia = (): void => { if (dpWrap) dpWrap.style.display = rpVal(dpEl.value) > 0 ? '' : 'none'; };
  dpEl.addEventListener('input', syncDpVia); syncDpVia();

  // ── Penyelamat ketikan (mati lampu / internet putus) ──────────────────────
  // Draft per-CABANG: nomor servis & daftar teknisi berbeda tiap cabang, jadi draft cabang lain
  // tak boleh menimpa form yang sedang dibuka.
  // Sertakan ID pengguna: localStorage milik PERANGKAT, bukan orang. Tanpa ini, CS berikutnya
  // yang login di komputer counter yang sama bisa memulihkan (dan melihat) isian CS sebelumnya —
  // termasuk nama/HP/alamat pelanggan.
  const panel = (root.querySelector('.panel') ?? root) as HTMLElement;
  // Isian saat simpan GAGAL karena koneksi. Dipakai untuk memutuskan apakah boleh dikirim
  // otomatis saat online: kalau staf sudah mengubah isian sesudah itu, mengirim diam-diam
  // berarti menyimpan yang salah lalu mengosongkan form — yang sedang diketik ikut hilang.
  let gagalSnapshot: string | null = null;
  const draftId = `intake:${u.id}:${outlet}`;
  const bar = $('f_draftbar');
  const tulisBar = (html: string): void => { bar.innerHTML = html; };
  const draft = readDraft(draftId);
  if (draft) {
    tulisBar(`<div class="scope-note" style="background:var(--warnbg);color:var(--warn);border-radius:8px;padding:8px 10px;margin:6px 0">
        📝 <b>Ada isian yang belum selesai</b> dari ${esc(draft.meta.at.slice(0, 16).replace('T', ' '))} — belum tersimpan sebagai tiket.
        <button class="btn" id="f_dpulih" style="padding:3px 10px;margin-left:6px">Lanjutkan isian</button>
        <button class="btn" id="f_dbuang" style="padding:3px 10px">Buang</button></div>`);
    bar.querySelector('#f_dpulih')?.addEventListener('click', () => { applyDraft(panel, draft.data); tulisBar(''); toast('📝 Isian dipulihkan'); });
    bar.querySelector('#f_dbuang')?.addEventListener('click', () => { clearDraft(draftId); tulisBar(''); });
  }
  // Pasang ke .panel (isi form), BUKAN root/#content: #content dipakai bersama SEMUA menu dan
  // hanya isinya yang diganti — listener yang menempel di sana tetap hidup setelah pindah menu,
  // lalu menimpa draft intake dengan isian layar lain (mis. form pembayaran).
  const stopAutosave = autosave(panel, draftId, 'Terima Servis');
  // Koneksi putus: beri tahu SEBELUM staf menekan simpan & gagal — isian tetap aman di draft.
  const statusKoneksi = (): void => {
    if (!bar.isConnected || isOnline()) return; // layar sudah berganti → jangan menulis ke DOM mati
    tulisBar(`<div class="scope-note" style="background:var(--dangerbg);color:var(--danger);border-radius:8px;padding:8px 10px;margin:6px 0">
        📴 <b>Tidak ada koneksi.</b> Isian Anda tersimpan otomatis di perangkat — begitu internet kembali, tekan Simpan lagi.</div>`);
  };
  statusKoneksi();
  // Koneksi pulih: kirim ULANG otomatis, tapi HANYA bila layar ini masih terbuka dan staf
  // memang sempat gagal menyimpan. Tanpa syarat itu, isian lama bisa terkirim jadi tiket saat
  // staf sudah pindah layar (atau sudah menginput ulang di perangkat lain) → tiket dobel.
  const offOffline = onBackOnline(() => {
    tulisBar('');
    if (bolehKirimOtomatis(gagalSnapshot, collectForm(panel), bar.isConnected)) {
      gagalSnapshot = null;
      toast('🌐 Koneksi kembali — menyimpan otomatis…');
      void simpan();
      return;
    }
    if (gagalSnapshot != null && bar.isConnected) {   // gagal tadi, tapi isian sudah diubah sesudahnya
      tulisBar(`<div class="scope-note" style="background:var(--okbg);color:var(--ok);border-radius:8px;padding:8px 10px;margin:6px 0">
          🌐 <b>Koneksi kembali.</b> Isian sudah Anda ubah setelah gagal tadi — periksa sekali lagi lalu tekan <b>Simpan</b>.</div>`);
      return;
    }
    toast('🌐 Koneksi kembali');
  });
  window.addEventListener('offline', statusKoneksi);
  // Form di-render ulang tiap ganti cabang/simpan → lepas listener lama.
  (root as HTMLElement & { __intakeCleanup?: () => void }).__intakeCleanup?.();
  (root as HTMLElement & { __intakeCleanup?: () => void }).__intakeCleanup = (): void => {
    stopAutosave(); offOffline(); window.removeEventListener('offline', statusKoneksi);
  };

  const simpan = async (abaikanKembar = false): Promise<void> => {
    const err = $('f_err'); const btn = $('f_save') as HTMLButtonElement;
    err.textContent = ''; btn.disabled = true;
    try {
      const kelLainTxt = (($('f_kellain') as HTMLInputElement | null)?.value ?? '').trim();
      const kel = Array.from(root.querySelectorAll<HTMLInputElement>('.f_kel:checked')).map((c) => (c.value === 'Lainnya' && kelLainTxt) ? `Lainnya: ${kelLainTxt}` : c.value).join(', ');
      const form: IntakeForm = {
        outlet, no: val('f_no'),
        custId: baru.checked ? null : (+val('f_cust') || null),
        newCust: baru.checked
          ? { nama: val('f_nnama'), kat: val('f_nkat') === 'Toko' ? 'Toko' : 'User', wa: val('f_nwa'), telp: val('f_ntelp'), lahir: val('f_nlahir'), email: val('f_nemail'), kota: val('f_nkota'), alamat: val('f_nalamat') }
          : null,
        jenis: val('f_jenis'), merk: val('f_merk'), model: val('f_model'), sn: val('f_sn'), warna: val('f_warna'),
        keluhan: val('f_keluhan'), kelengkapan: kel, kondisi: val('f_kondisi'), sandi: val('f_sandi'),
        catatan: val('f_catatan'), estBiaya: rpVal(val('f_est')), estSelesai: val('f_estdate'),
        dp: rpVal(val('f_dp')), dpVia: rpVal(val('f_dp')) > 0 ? val('f_dpvia') : '', prioritas: val('f_prio'), teknisi: val('f_tek'),
        dugaan: +val('f_dugaan') || null,
        caseKhusus: (root.querySelector('#f_case') as HTMLInputElement).checked,
        caseKhususKet: val('f_caseket'),
        fotoUnit: foto.nilai(),
        abaikanKembar,
      };
      const no = await submitIntake(form);
      // Hentikan autosave DULU: timer 400ms yang masih menggantung akan menulis ulang draft
      // sesudah dihapus → bar "isian belum selesai" muncul untuk tiket yang sudah jadi.
      stopAutosave();
      clearDraft(draftId);              // tersimpan sebagai tiket → draft tak perlu lagi
      toast(`✅ Tersimpan: ${no}`);
      refreshCounts();
      void renderIntake(root);
      offerTandaTerima(no);
    } catch (e) {
      btn.disabled = false;
      // Unit yang sama sudah punya tiket berjalan (kasir lain baru saja menginputnya).
      // Bukan penolakan — staf yang tahu apakah ini benar-benar dua unit berbeda.
      if (e instanceof TiketKembar) {
        const l = e.lain;
        if (confirm(`Unit ini sudah punya tiket berjalan:\n\n${l.no} · ${l.status}${l.penerima ? ` · diterima oleh ${l.penerima}` : ''}\n\nLanjut membuat tiket BARU untuk unit yang sama?`)) {
          await simpan(true);
        } else {
          err.textContent = `Dibatalkan — buka tiket ${l.no} yang sudah ada.`;
        }
        return;
      }
      // Isian TIDAK dihapus — draft tetap tersimpan, tinggal tekan Simpan lagi saat koneksi pulih.
      gagalSnapshot = isOnline() ? null : JSON.stringify(collectForm(panel));
      err.textContent = isOnline() ? (e as Error).message
        : '📴 Tidak ada koneksi — isian aman tersimpan di perangkat. Akan disimpan otomatis begitu internet kembali (layar ini jangan ditutup).';
    }
  };
  $('f_save').addEventListener('click', () => void simpan());
}

/** Pasca-simpan: tawarkan cetak Tanda Terima / kirim WA (manual — tak auto-print, port terimaSaved v0.18). */
function offerTandaTerima(no: string): void {
  const bg = openModal(`<div class="modal-head"><h3>✅ Servis diterima — ${esc(no)}</h3><button class="x">×</button></div>
    <div class="modal-body"><p>Unit tercatat. Cetak <b>Tanda Terima</b> atau kirim link lacak via WhatsApp (opsional).</p></div>
    <div class="modal-foot"><button class="btn" id="ot_close">Simpan tanpa cetak</button><button class="btn" id="ot_wa">💬 Kirim WA</button><button class="btn primary" id="ot_print">📄 Preview / Cetak Tanda Terima</button></div>`);
  bg.querySelector('#ot_close')?.addEventListener('click', () => bg.remove());
  bg.querySelector('#ot_print')?.addEventListener('click', () => { bg.remove(); void previewTandaTerima(no); });
  bg.querySelector('#ot_wa')?.addEventListener('click', async () => {
    try {
      const c = await repo.getTicketContact(no);
      if (!c.wa) { toast('Nomor WA pelanggan belum ada.'); return; }
      const track = `https://ifics.app/t/${c.token}`;
      openWA(c.wa, fillTpl(waTpl().terima, { nama: c.nama, no, unit: c.unit, merk: c.merk, tipe: c.tipe, kerusakan: c.kerusakan, tracking: track }));
    } catch (e) { toast((e as Error).message); }
  });
}
